import {useState} from 'react'
import {useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {PageHeading} from '@/components/page-heading'
import {DataTable, type Column} from '@/components/data-table'
import {CeldaEstado} from '@/components/ui/celdas-tabla'
import {EntitySearchSelect} from '@/components/entity-search-select'
import {NativeSelect} from '@/components/native-select'
import {HelpIcon} from '@/components/help-icon'
import {PlanLockedPage} from '@/components/plan-upsell'
import {Button} from '@/components/ui/button'
import {Hint} from '@/components/ui/hint'
import {Label} from '@/components/ui/label'
import {PorQue} from '@/components/ui/por-que'
import {Tag} from '@/components/ui/tag'
import {Skeleton} from '@/components/ui/skeleton'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {useOperatorPage} from '@/components/no-access-state'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useEmployees} from '@/hooks/empresa/identidad/use-employees'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {useObras} from '@/hooks/empresa/obras/use-obras'
import {usePerfilesCargo} from '@/hooks/empresa/perfil_cargo/use-perfiles-cargo'
import {useTableState} from '@/hooks/common/use-table-state'
import {
  useCarpetasVisita,
  useDescargarCarpetaVisita,
  useGenerarCarpetaVisita,
  type CarpetaAlcanceTipo,
  type CarpetaVisita,
  type CarpetaVisitaEstado,
} from '@/hooks/salud/carpeta_visita/use-carpetas-visita'
import {translateApiError} from '@/lib/errors'
import {formatDate} from '@/lib/risk'

const CARPETAS_PAGE_SIZE = 20
const ALCANCES: CarpetaAlcanceTipo[] = ['empresa', 'cargo', 'persona', 'proyecto']

const ESTADO_TONE: Record<CarpetaVisitaEstado, 'green' | 'amber' | 'red'> = {
  listo: 'green',
  pendiente: 'amber',
  procesando: 'amber',
  error: 'red',
}

/** Cargos, personas y obras son cardinalidad N: combobox buscable (R3), nunca un desplegable nativo. */
function ReferenciaDelAlcance({
  businessId,
  alcance,
  valor,
  onPick,
}: {
  businessId: string
  alcance: CarpetaAlcanceTipo
  valor: {id: string; nombre: string} | null
  onPick: (opcion: {id: string; nombre: string} | null) => void
}) {
  const {t} = useTranslation()
  const [query, setQuery] = useState('')
  const cargos = usePerfilesCargo(alcance === 'cargo' ? businessId : undefined, {page: 1, search: query})
  const obras = useObras(alcance === 'proyecto' ? businessId : undefined, {page: 1, search: query})
  // Busca en el servidor y paginada: la excepción de R3 no cubre bajarse la nómina entera.
  const personas = useEmployees(alcance === 'persona' ? businessId : undefined, {
    page: 1,
    search: query,
    sortBy: 'name',
    sortDir: 'asc',
  })

  if (alcance === 'empresa') return null

  const opciones =
    alcance === 'cargo'
      ? (cargos.data?.items ?? []).map((fila) => ({id: fila.position_id, nombre: fila.cargo}))
      : alcance === 'proyecto'
        ? (obras.data?.items ?? []).map((fila) => ({id: fila.id, nombre: fila.name}))
        : (personas.data?.items ?? []).map((fila) => ({id: fila.id, nombre: `${fila.name} ${fila.last_name}`}))

  return (
    <div className="space-y-1.5">
      <Label htmlFor="carpeta-referencia">{t(`carpetaVisita.referencia.${alcance}`)}</Label>
      <EntitySearchSelect
        id="carpeta-referencia"
        items={opciones}
        value={valor}
        getKey={(opcion) => opcion.id}
        filterText={(opcion) => opcion.nombre}
        renderOption={(opcion) => opcion.nombre}
        renderTriggerValue={(opcion) => opcion.nombre}
        placeholder={t(`carpetaVisita.referencia.${alcance}`)}
        searchPlaceholder={t('carpetaVisita.buscarReferencia')}
        noResultsLabel={t('carpetaVisita.sinResultados')}
        testId="carpeta-referencia"
        onQueryChange={setQuery}
        onPick={onPick}
      />
    </div>
  )
}

/** No pregunta por el detalle de restricciones: lo decide la custodia de salud en el servidor (§4).
 * Pide siempre `regenerar`: quien aprieta quiere la carpeta de hoy. */
function GenerarDialog({
  businessId,
  custodia,
  ultimaLista,
  abierto,
  onCerrar,
}: {
  businessId: string
  custodia: boolean
  ultimaLista: (alcance: CarpetaAlcanceTipo, referenciaId: string | null) => string | undefined
  abierto: boolean
  onCerrar: () => void
}) {
  const {t} = useTranslation()
  const [dialogo, setDialogo] = useState<HTMLElement | null>(null)
  const [alcance, setAlcance] = useState<CarpetaAlcanceTipo>('empresa')
  const [referencia, setReferencia] = useState<{id: string; nombre: string} | null>(null)
  const [error, setError] = useState<string | null>(null)
  const generar = useGenerarCarpetaVisita(businessId)
  const faltaReferencia = alcance !== 'empresa' && referencia === null
  const desde = ultimaLista(alcance, referencia?.id ?? null)

  function pedir() {
    setError(null)
    generar.mutate(
      {alcanceTipo: alcance, alcanceRefId: referencia?.id ?? null, regenerar: true},
      {onSuccess: onCerrar, onError: (err) => setError(translateApiError(err))}
    )
  }

  return (
    <Dialog open={abierto} onOpenChange={(open) => !open && onCerrar()}>
      <DialogContent ref={setDialogo} data-testid="carpeta-generar-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1.5">
            {t('carpetaVisita.generar.titulo')}
            <HelpIcon label={t('carpetaVisita.generar.queTraeLabel')} testId="carpeta-que-trae" contenedor={dialogo}>
              {t('carpetaVisita.generar.queTrae')}
            </HelpIcon>
          </DialogTitle>
          <DialogDescription>{t('carpetaVisita.generar.descripcion')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="carpeta-alcance">{t('carpetaVisita.generar.alcance')}</Label>
            <NativeSelect
              id="carpeta-alcance"
              data-testid="carpeta-alcance"
              value={alcance}
              onChange={(event) => {
                setAlcance(event.target.value as CarpetaAlcanceTipo)
                setReferencia(null)
              }}>
              {ALCANCES.filter((opcion) => custodia || opcion !== 'persona').map((opcion) => (
                <option key={opcion} value={opcion}>
                  {t(`carpetaVisita.alcances.${opcion}`)}
                </option>
              ))}
            </NativeSelect>
            {!custodia && <Hint testId="carpeta-sin-custodia">{t('carpetaVisita.generar.sinCustodia')}</Hint>}
            <PorQue
              label={t('carpetaVisita.generar.custodiaPorqueLabel')}
              testId="carpeta-custodia-porque"
              contenidoTestId="carpeta-custodia-porque-contenido"
              contenedor={dialogo}>
              {t(custodia ? 'carpetaVisita.generar.custodiaPorqueCustodio' : 'carpetaVisita.generar.custodiaPorque')}
            </PorQue>
          </div>
          <ReferenciaDelAlcance businessId={businessId} alcance={alcance} valor={referencia} onPick={setReferencia} />
          {error && (
            <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="carpeta-generar-error">
              {error}
            </p>
          )}
          <Button
            data-testid="carpeta-generar-confirmar"
            disabled={faltaReferencia || generar.isPending}
            onClick={pedir}>
            {desde
              ? t('carpetaVisita.generar.volverAArmar', {fecha: formatDate(desde)})
              : t('carpetaVisita.generar.accion')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** Lista compartida a propósito: ver la carpeta que pidió otra persona evita dos armados (E10-H15). */
export function CarpetasVisitaPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const {business, guard} = useOperatorPage(businessId)
  const capabilities = useCapabilities(businessId)
  const godReadOnly = useGodModeReadOnly()
  const table = useTableState('created_at')
  // Solo `page`: el endpoint no honra search ni sort; el orden es del servidor, la última arriba.
  const lista = useCarpetasVisita(businessId, {page: table.page, search: ''})
  const descargar = useDescargarCarpetaVisita(businessId!)
  const generar = useGenerarCarpetaVisita(businessId!)
  const [dialogo, setDialogo] = useState(false)

  // El guard antes que la puerta del plan: `useCapabilities` devuelve plan_tier 'free' mientras
  // `useBusiness` está en vuelo, y al revés parpadearía el upsell (mismo orden que `obras.tsx`).
  if (guard) return guard
  // Las tres rutas llevan `require_paid_business`: sin esta puerta un tenant gratuito
  // recibe 402 y se queda con el esqueleto girando, sin mensaje y sin ruta de compra.
  if (capabilities.plan_tier === 'free') {
    return (
      <PlanLockedPage
        feature={t('plans.feature.carpetaVisita')}
        businessId={businessId}
        businessName={business!.name}
      />
    )
  }

  const puedeGenerar = capabilities.sgsst_operar && !godReadOnly
  // El alcance de una persona es para el detalle de salud: solo lo ofrece a quien lo custodia.
  const custodia = capabilities.sgsst_custodiar_salud
  const ultimaLista = (alcance: CarpetaAlcanceTipo, referenciaId: string | null) =>
    (lista.data?.items ?? []).find(
      (fila) =>
        fila.estado === 'listo' && fila.alcance_tipo === alcance && (fila.alcance_ref_id ?? null) === referenciaId
    )?.created_at

  const columnas: Column<CarpetaVisita>[] = [
    {
      key: 'alcance',
      header: t('carpetaVisita.tabla.alcance'),
      cell: (fila) => (
        <span data-testid={`carpeta-alcance-${fila.id}`}>
          {t(`carpetaVisita.alcances.${fila.alcance_tipo}`)}
          {fila.alcance_nombre ? ` · ${fila.alcance_nombre}` : ''}
        </span>
      ),
    },
    {
      key: 'estado',
      header: t('carpetaVisita.tabla.estado'),
      width: 'w-1/4',
      cell: (fila) => (
        <CeldaEstado
          chip={
            <Tag tone={ESTADO_TONE[fila.estado]} data-testid={`carpeta-estado-${fila.id}`}>
              {t(`carpetaVisita.estados.${fila.estado}`)}
            </Tag>
          }
          meta={
            fila.estado === 'error' ? (
              <span data-testid={`carpeta-error-${fila.id}`}>
                {t('carpetaVisita.errorAyuda')}
                {fila.error_detalle ? ` (${fila.error_detalle})` : ''}
              </span>
            ) : undefined
          }
        />
      ),
    },
    {
      key: 'detalle',
      header: t('carpetaVisita.tabla.detalle'),
      width: 'w-32',
      cell: (fila) => (
        <span className="text-xs text-muted-foreground" data-testid={`carpeta-detalle-${fila.id}`}>
          {t(fila.sin_detalle_restricciones ? 'carpetaVisita.sinDetalle' : 'carpetaVisita.conDetalle')}
        </span>
      ),
    },
    {
      key: 'generado',
      header: t('carpetaVisita.tabla.generado'),
      width: 'w-1/4',
      cell: (fila) => (
        <span className="font-mono text-xs">
          {formatDate(fila.created_at)}
          {fila.generado_por ? ` · ${fila.generado_por}` : ''}
        </span>
      ),
    },
    {
      key: 'accion',
      header: '',
      width: 'w-40',
      cell: (fila) =>
        fila.estado === 'error' ? (
          <Button
            size="sm"
            variant="outline"
            disabled={!puedeGenerar || generar.isPending}
            data-testid={`carpeta-reintentar-${fila.id}`}
            onClick={() =>
              generar.mutate({alcanceTipo: fila.alcance_tipo, alcanceRefId: fila.alcance_ref_id, regenerar: true})
            }>
            {t('carpetaVisita.reintentar')}
          </Button>
        ) : (
          // Con detalle solo la descarga quien custodia; ofrecerla a otro sería un 403 ya sabido.
          <Button
            size="sm"
            variant="outline"
            disabled={fila.estado !== 'listo' || descargar.isPending || (!fila.sin_detalle_restricciones && !custodia)}
            title={
              !fila.sin_detalle_restricciones && !custodia
                ? t('carpetaVisita.errors.carpetaDetalleSoloResponsable')
                : undefined
            }
            data-testid={`carpeta-descargar-${fila.id}`}
            onClick={() => descargar.mutate(fila.id)}>
            {t('carpetaVisita.descargar')}
          </Button>
        ),
    },
  ]

  return (
    <div className="space-y-5" data-testid="carpetas-visita">
      <PageHeading
        title={t('carpetaVisita.title')}
        subtitle={business!.name}
        accion={
          puedeGenerar && (
            <Button size="sm" data-testid="carpeta-generar" onClick={() => setDialogo(true)}>
              {t('carpetaVisita.generar.accion')}
            </Button>
          )
        }
      />
      {descargar.isError && (
        <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="carpeta-descarga-error">
          {translateApiError(descargar.error)}
        </p>
      )}

      {generar.isError && (
        <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="carpeta-reintento-error">
          {translateApiError(generar.error)}
        </p>
      )}

      {/* react-query se rinde en silencio tras dos reintentos: sin esto el esqueleto gira para siempre. */}
      {lista.isError && (
        <div
          className="notice-amber max-w-2xl space-y-3 rounded-lg px-3 py-2 text-sm"
          data-testid="carpetas-visita-error">
          <p>{t('carpetaVisita.errorCargaLista')}</p>
          <Button size="sm" variant="outline" onClick={() => void lista.refetch()}>
            {t('carpetaVisita.reintentarCarga')}
          </Button>
        </div>
      )}
      {!lista.data && !lista.isError && <Skeleton className="h-40 w-full" />}
      {lista.data && (
        <>
          {/* Sin sortBy: el endpoint no ordena, así que ningún encabezado debe invitar a ordenar. */}
          <DataTable
            columns={columnas}
            rows={lista.data.items}
            rowKey={(fila) => fila.id}
            emptyState={
              <span data-testid="carpetas-visita-vacio">
                {t(puedeGenerar ? 'carpetaVisita.vacio' : 'carpetaVisita.vacioSoloLectura')}
                {!puedeGenerar && (
                  <>
                    {' '}
                    <PorQue label={t('common.porQue')} testId="carpetas-visita-vacio-porque">
                      <p>{t('carpetaVisita.vacioSoloLecturaPorque')}</p>
                    </PorQue>
                  </>
                )}
              </span>
            }
          />
          <PaginationBar
            page={table.page}
            pageSize={CARPETAS_PAGE_SIZE}
            total={lista.data.total}
            onPageChange={table.setPage}
          />
        </>
      )}

      {/* `key` para remontarlo limpio: guarda alcance, referencia y error en su propio estado. */}
      <GenerarDialog
        key={dialogo ? 'abierto' : 'cerrado'}
        businessId={businessId!}
        custodia={custodia}
        ultimaLista={ultimaLista}
        abierto={dialogo}
        onCerrar={() => setDialogo(false)}
      />
    </div>
  )
}
